import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { coproprietesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Building2, Plus, Trash2, ChevronRight, Loader2, Zap, HardHat } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { name: '', address: '', city: '', postal_code: '', create_chantier: false };

export const CoproprietesSection = ({ prospectId, enseigne }) => {
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [adding, setAdding] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);

    const load = useCallback(async () => {
        try {
            const { data } = await coproprietesAPI.list(prospectId);
            setItems(data);
        } catch {
            setItems([]);
        }
    }, [prospectId]);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        const name = form.name.trim();
        if (!name) { toast.error('Nom de la copropriété requis'); return; }
        setAdding(true);
        try {
            await coproprietesAPI.add(prospectId, { ...form, name });
            toast.success(`Copropriété « ${name} » créée`);
            setForm(EMPTY);
            setShowForm(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Création impossible');
        } finally {
            setAdding(false);
        }
    };

    const remove = async (copro) => {
        if (!window.confirm(`Mettre la copropriété « ${copro.name} » à la corbeille ?`)) return;
        try {
            await coproprietesAPI.remove(prospectId, copro.id);
            toast.success('Copropriété supprimée');
            await load();
        } catch {
            toast.error('Suppression impossible');
        }
    };

    return (
        <Card data-testid="coproprietes-section" className="border-[#1E5BFF]/30 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="flex items-center gap-2 text-[#0B1F3B] text-lg">
                    <Building2 className="w-5 h-5 text-[#1E5BFF]" />
                    Copropriétés {items ? `(${items.length})` : ''}
                </CardTitle>
                <Button size="sm" onClick={() => setShowForm((v) => !v)} className="bg-[#1E5BFF] hover:bg-[#1747cc]" data-testid="copro-add-toggle">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">
                    Chaque copropriété est un <strong>dossier autonome</strong> (Easy Power &amp; Chantier indépendants), rattaché au syndic <strong>{enseigne}</strong>.
                </p>

                {showForm && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="copro-add-form">
                        <Input placeholder="Nom de la copropriété *" value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="copro-name-input" />
                        <Input placeholder="Adresse" value={form.address}
                            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="copro-address-input" />
                        <div className="flex gap-2">
                            <Input placeholder="Ville" value={form.city}
                                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="copro-city-input" />
                            <Input placeholder="CP" className="w-28" value={form.postal_code}
                                onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} data-testid="copro-cp-input" />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                            <input type="checkbox" checked={form.create_chantier}
                                onChange={(e) => setForm((f) => ({ ...f, create_chantier: e.target.checked }))} data-testid="copro-chantier-check" />
                            Créer aussi un dossier Chantier pour cette copropriété
                        </label>
                        <Button onClick={submit} disabled={adding} className="w-full bg-[#0B1F3B] hover:bg-[#0B1F3B]/90" data-testid="copro-submit">
                            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer la copropriété'}
                        </Button>
                    </div>
                )}

                {items === null && <p className="text-sm text-slate-400 py-4 text-center">Chargement…</p>}
                {items && items.length === 0 && (
                    <p className="text-sm text-slate-400 py-4 text-center" data-testid="copro-empty">
                        Aucune copropriété pour l'instant. Cliquez sur « Ajouter ».
                    </p>
                )}
                {items && items.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-[#1E5BFF]/40 transition-colors" data-testid={`copro-row-${c.id}`}>
                        <button onClick={() => navigate(`/prospects/${c.id}`)} className="flex-1 text-left min-w-0" data-testid={`copro-open-${c.id}`}>
                            <p className="font-semibold text-[#0B1F3B] truncate">{c.name}</p>
                            <p className="text-xs text-slate-500 truncate">{[c.address, c.postal_code, c.city].filter(Boolean).join(' · ') || '—'}</p>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="inline-flex items-center gap-1 text-[11px] text-purple-700"><Zap className="w-3 h-3" /> Easy Power</span>
                                {c.chantier_count > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-orange-700"><HardHat className="w-3 h-3" /> {c.chantier_count} chantier(s)</span>
                                )}
                            </div>
                        </button>
                        <button onClick={() => remove(c)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`copro-delete-${c.id}`} aria-label="Supprimer la copropriété">
                            <Trash2 className="w-4 h-4" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

export default CoproprietesSection;
